import React, {
  useState,
  useMemo,
  useEffect,
  createContext,
  useContext,
} from "react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppRoutes } from "./Routes";
import "./styles/theme.css";
import { ChatProvider } from "./contexts/ChatContext";
import FloatingChat from "./components/chat/FloatingChat";
import ReminderChecker from "./components/reminders/ReminderChecker";
// QUITAR: import { useCollection } from "./hooks/useCollection";
// QUITAR: import { useFirestore } from "./hooks/useFirestore";

// ============================================================
// COLORES CORPORATIVOS EPC
// ============================================================
const epcColors = {
  azules: {
    dark: "#005f65",
    medium: "#00afa3",
    light: "#00e1d2",
    lighter: "#42fff2",
    lightest: "#95ffef",
  },
  verdes: {
    dark: "#0e502d",
    medium: "#00935d",
    light: "#00c07c",
    lighter: "#00ffbb",
    lightest: "#56ffd2",
  },
};

// ============================================================
// CONTEXTO DE TEMA
// ============================================================
export const ThemeContext = createContext();

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error(
      "useThemeContext debe usarse dentro de un ThemeContextProvider",
    );
  }
  return context;
};

// ============================================================
// HOOK PERSONALIZADO PARA MODO OSCURO
// ============================================================
const useDarkMode = () => {
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("epc-dark-mode");
      if (saved !== null) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error("Error al leer el modo oscuro del localStorage:", error);
    }
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  });

  useEffect(() => {
    try {
      localStorage.setItem("epc-dark-mode", JSON.stringify(darkMode));
    } catch (error) {
      console.error("Error al guardar el modo oscuro en localStorage:", error);
    }

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#0a1929" : "#005f65");
    } else {
      const meta = document.createElement("meta");
      meta.name = "theme-color";
      meta.content = darkMode ? "#0a1929" : "#005f65";
      document.head.appendChild(meta);
    }
  }, [darkMode]);

  const toggleTheme = () => {
    setDarkMode((prev) => !prev);
  };

  return [darkMode, toggleTheme];
};

// ============================================================
// COMPONENTE DE CARGA
// ============================================================
const LoadingScreen = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      backgroundColor: "#f5f7fa",
    }}
  >
    <div style={{ textAlign: "center" }}>
      <img
        src="/epcorelogo.png"
        alt="EPC"
        style={{ width: "120px", height: "auto", marginBottom: "20px" }}
      />
      <div
        style={{
          width: "40px",
          height: "40px",
          border: "4px solid #e0e0e0",
          borderTop: "4px solid #005f65",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto",
        }}
      />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
);

// ============================================================
// COMPONENTE PARA CONTENIDO QUE REQUIERE AUTENTICACIÓN
// ============================================================
const AuthenticatedContent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <AppRoutes />;
  }

  return (
    <ChatProvider>
      <AppRoutes />
      <FloatingChat />
      <ReminderChecker />
    </ChatProvider>
  );
};

// ============================================================
// COMPONENTE PRINCIPAL APP
// ============================================================
function App() {
  const [darkMode, toggleTheme] = useDarkMode();

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? epcColors.azules.light : epcColors.azules.dark,
            light: darkMode ? epcColors.azules.lighter : epcColors.azules.medium,
            dark: darkMode ? epcColors.azules.medium : "#004a4f",
            contrastText: "#ffffff",
          },
          secondary: {
            main: darkMode ? epcColors.verdes.light : epcColors.verdes.medium,
            light: darkMode ? epcColors.verdes.lighter : epcColors.verdes.light,
            dark: darkMode ? epcColors.verdes.medium : epcColors.verdes.dark,
            contrastText: "#ffffff",
          },
          success: { main: epcColors.verdes.medium, light: epcColors.verdes.lightest, dark: epcColors.verdes.dark },
          info: { main: epcColors.azules.medium, light: epcColors.azules.lighter, dark: epcColors.azules.dark },
          warning: {
  main: "#C77DFF",
  light: "#E0AAFF",
  dark: "#9D4EDD",
},
          error: { main: "#f44336", light: "#e57373", dark: "#d32f2f" },
          background: darkMode
            ? { default: "#0a1929", paper: "#0d2137" }
            : { default: "#f5f7fa", paper: "#ffffff" },
          text: darkMode
            ? { primary: "#e0e0e0", secondary: "#b0b0b0", disabled: "#6c6c6c" }
            : { primary: "#1a1f36", secondary: "#697386", disabled: "#bdbdbd" },
          divider: darkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 95, 101, 0.12)",
        },
        typography: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          h1: { fontWeight: 700, fontSize: "2.25rem", lineHeight: 1.2 },
          h2: { fontWeight: 700, fontSize: "1.875rem", lineHeight: 1.2 },
          h3: { fontWeight: 600, fontSize: "1.5rem", lineHeight: 1.2 },
          h4: { fontWeight: 600, fontSize: "1.25rem", lineHeight: 1.2 },
          h5: { fontWeight: 500, fontSize: "1rem", lineHeight: 1.2 },
          h6: { fontWeight: 500, fontSize: "0.875rem", lineHeight: 1.2 },
          body1: { fontSize: "1rem", lineHeight: 1.5 },
          body2: { fontSize: "0.875rem", lineHeight: 1.5 },
          caption: { fontSize: "0.75rem", lineHeight: 1.5 },
          button: { textTransform: "none", fontWeight: 500 },
        },
        shape: { borderRadius: 8 },
        components: {
          MuiButton: {
            styleOverrides: {
              root: { textTransform: "none", fontWeight: 500, borderRadius: 8, padding: "8px 16px" },
              contained: {
                boxShadow: darkMode ? "0 2px 8px rgba(0, 225, 210, 0.2)" : "0 2px 8px rgba(0, 95, 101, 0.15)",
                "&:hover": {
                  boxShadow: darkMode ? "0 4px 12px rgba(0, 225, 210, 0.3)" : "0 4px 12px rgba(0, 95, 101, 0.25)",
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.08)",
                transition: "box-shadow 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: darkMode ? "0 4px 16px rgba(0,0,0,0.4)" : "0 4px 12px rgba(0,0,0,0.12)",
                },
              },
            },
          },
          MuiDrawer: { styleOverrides: { paper: { border: "none" } } },
          MuiAppBar: {
            styleOverrides: {
              root: { boxShadow: darkMode ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.08)" },
            },
          },
          MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
          MuiTextField: { styleOverrides: { root: { "& .MuiOutlinedInput-root": { borderRadius: 8 } } } },
          MuiChip: { styleOverrides: { root: { borderRadius: 8 } } },
          MuiDialog: { styleOverrides: { paper: { borderRadius: 16 } } },
        },
      }),
    [darkMode],
  );

  const themeContextValue = useMemo(
    () => ({ darkMode, toggleTheme, epcColors }),
    [darkMode, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <AuthenticatedContent />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ThemeContext.Provider>
  );
}

export default App;